import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  briefingTimeOfDaySchema,
  fetchBriefingSubscription,
  saveBriefingSubscription,
  type BriefingSubscriptionInput,
} from './briefing-api'

function formatLastRun(lastRunOn: string): string {
  const [year, month, day] = lastRunOn.split('-').map(Number)
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`
}

export function BriefingCard() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState(true)
  const [lastRunOn, setLastRunOn] = useState<string | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [timeOfDay, setTimeOfDay] = useState('07:00')
  const [emailEnabled, setEmailEnabled] = useState(false)
  const [saved, setSaved] = useState<BriefingSubscriptionInput>({ enabled: false, time_of_day: '07:00', email_enabled: false })

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await fetchBriefingSubscription()
        if (cancelled) return
        const subscription = data.subscription
        const current = {
          enabled: subscription.enabled,
          time_of_day: subscription.time_of_day,
          email_enabled: subscription.email_enabled,
        }
        setEnabled(current.enabled)
        setTimeOfDay(current.time_of_day)
        setEmailEnabled(current.email_enabled)
        setSaved(current)
        setEmailAvailable(data.email_available)
        setLastRunOn(subscription.last_run_on)
      } catch (error) {
        if (!cancelled) toast.error(error instanceof Error ? error.message : 'Briefing-Einstellungen konnten nicht geladen werden.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const current: BriefingSubscriptionInput = { enabled, time_of_day: timeOfDay, email_enabled: emailEnabled }
  const unchanged =
    current.enabled === saved.enabled &&
    current.time_of_day === saved.time_of_day &&
    current.email_enabled === saved.email_enabled

  const timeValid = briefingTimeOfDaySchema.safeParse(timeOfDay).success

  async function handleSave() {
    setSaving(true)
    try {
      const data = await saveBriefingSubscription(current)
      setSaved(current)
      setLastRunOn(data.subscription.last_run_on)
      toast.success('Briefing-Einstellungen gespeichert.')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Briefing-Einstellungen konnten nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tägliches Briefing</CardTitle>
        <CardDescription>
          MindKeep legt jeden Tag ein Briefing mit neuen Dateien, offenen Fragen und Vorschlägen in den
          Eingang (_Inbox/Briefings/). Gilt für den aktiven Workspace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <input
                id="briefing-enabled"
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
              <Label htmlFor="briefing-enabled">Briefing aktivieren</Label>
            </div>
            <div className="grid gap-1 min-[761px]:grid-cols-3 min-[761px]:gap-3 min-[761px]:items-center">
              <Label htmlFor="briefing-time">Uhrzeit (Europe/Zurich)</Label>
              <Input
                id="briefing-time"
                type="time"
                step={60}
                className="min-[761px]:col-span-2 min-[761px]:w-48"
                value={timeOfDay}
                onChange={(event) => setTimeOfDay(event.target.value)}
                disabled={!enabled}
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <input
                  id="briefing-email"
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={emailEnabled}
                  onChange={(event) => setEmailEnabled(event.target.checked)}
                  disabled={!emailAvailable}
                />
                <Label htmlFor="briefing-email">Zusätzlich per E-Mail (nur Titel und Links, keine Inhalte)</Label>
              </div>
              {!emailAvailable ? (
                <p className="text-xs text-muted-foreground">
                  E-Mail-Versand ist auf diesem Server nicht eingerichtet.
                </p>
              ) : null}
            </div>
            {lastRunOn ? (
              <p className="text-sm text-muted-foreground">Letztes Briefing: {formatLastRun(lastRunOn)}</p>
            ) : null}
            <Button type="button" onClick={() => void handleSave()} disabled={saving || unchanged || !timeValid}>
              Speichern
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
